import {
  parseAdminMember,
  parseAdminMemberInvite,
  parseAdminMemberInvitesResponse,
  parseAdminMembersResponse,
  parseAdminStudentSelfInvite,
  parseAdminStudentSelfInvitesResponse,
  parseCreateAdminMemberInviteRequest,
  parseCreateAdminMemberInviteResponse,
  parseCreateAdminMemberRequest,
  parseCreateAdminStudentSelfInviteRequest,
  parseCreateAdminStudentSelfInviteResponse,
  parseCreateStudioRequest,
  parseStudio,
  parseStudiosResponse,
  parseUpdateAdminMemberRequest,
  parseUpdateStudioRequest
} from "@madamuh/domain";
import type {
  AdminMemberDto,
  AdminMemberInviteDto,
  AdminMemberInvitesResponseDto,
  AdminMembersResponseDto,
  AdminStudentSelfInviteDto,
  AdminStudentSelfInvitesResponseDto,
  CreateAdminMemberInviteRequestDto,
  CreateAdminMemberInviteResponseDto,
  CreateAdminMemberRequestDto,
  CreateAdminStudentSelfInviteRequestDto,
  CreateAdminStudentSelfInviteResponseDto,
  CreateStudioRequestDto,
  StudioDto,
  StudiosResponseDto,
  UpdateAdminMemberRequestDto,
  UpdateStudioRequestDto
} from "@madamuh/domain";
import { apiGet, apiPatch, apiPost } from "./client";

export async function fetchAdminMembers(tenantSlug: string): Promise<AdminMembersResponseDto> {
  const response = await apiGet<unknown>(`/api/v1/admin/tenants/${tenantSlug}/members`);
  return parseAdminMembersResponse(response);
}

export async function fetchAdminMember(tenantSlug: string, platformUserId: string): Promise<AdminMemberDto> {
  const response = await apiGet<unknown>(`/api/v1/admin/tenants/${tenantSlug}/members/${platformUserId}`);
  return parseAdminMember(response);
}

export async function createAdminMember(
  tenantSlug: string,
  request: CreateAdminMemberRequestDto
): Promise<AdminMemberDto> {
  const parsedRequest = parseCreateAdminMemberRequest(request);
  const response = await apiPost<CreateAdminMemberRequestDto, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/members`,
    parsedRequest
  );

  return parseAdminMember(response);
}

export async function patchAdminMember(
  tenantSlug: string,
  platformUserId: string,
  request: UpdateAdminMemberRequestDto
): Promise<AdminMemberDto> {
  const parsedRequest = parseUpdateAdminMemberRequest(request);
  const response = await apiPatch<UpdateAdminMemberRequestDto, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/members/${platformUserId}`,
    parsedRequest
  );

  return parseAdminMember(response);
}

export async function fetchAdminMemberInvites(tenantSlug: string): Promise<AdminMemberInvitesResponseDto> {
  const response = await apiGet<unknown>(`/api/v1/admin/tenants/${tenantSlug}/member-invites`);
  return parseAdminMemberInvitesResponse(response);
}

export async function createAdminMemberInvite(
  tenantSlug: string,
  request: CreateAdminMemberInviteRequestDto
): Promise<CreateAdminMemberInviteResponseDto> {
  const parsedRequest = parseCreateAdminMemberInviteRequest(request);
  const response = await apiPost<CreateAdminMemberInviteRequestDto, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/member-invites`,
    parsedRequest
  );

  return parseCreateAdminMemberInviteResponse(response);
}

export async function revokeAdminMemberInvite(
  tenantSlug: string,
  inviteId: string
): Promise<AdminMemberInviteDto> {
  const response = await apiPost<Record<string, never>, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/member-invites/${inviteId}/revoke`,
    {}
  );

  return parseAdminMemberInvite(response);
}

export async function fetchAdminStudentSelfInvites(
  tenantSlug: string
): Promise<AdminStudentSelfInvitesResponseDto> {
  const response = await apiGet<unknown>(`/api/v1/admin/tenants/${tenantSlug}/student-self-invites`);
  return parseAdminStudentSelfInvitesResponse(response);
}

export async function createAdminStudentSelfInvite(
  tenantSlug: string,
  request: CreateAdminStudentSelfInviteRequestDto
): Promise<CreateAdminStudentSelfInviteResponseDto> {
  const parsedRequest = parseCreateAdminStudentSelfInviteRequest(request);
  const response = await apiPost<CreateAdminStudentSelfInviteRequestDto, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/student-self-invites`,
    parsedRequest
  );

  return parseCreateAdminStudentSelfInviteResponse(response);
}

export async function revokeAdminStudentSelfInvite(
  tenantSlug: string,
  inviteId: string
): Promise<AdminStudentSelfInviteDto> {
  const response = await apiPost<Record<string, never>, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/student-self-invites/${inviteId}/revoke`,
    {}
  );

  return parseAdminStudentSelfInvite(response);
}

export async function fetchAdminStudios(tenantSlug: string): Promise<StudiosResponseDto> {
  const response = await apiGet<unknown>(`/api/v1/admin/tenants/${tenantSlug}/studios`);
  return parseStudiosResponse(response);
}

export async function createAdminStudio(
  tenantSlug: string,
  request: CreateStudioRequestDto
): Promise<StudioDto> {
  const parsedRequest = parseCreateStudioRequest(request);
  const response = await apiPost<CreateStudioRequestDto, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/studios`,
    parsedRequest
  );

  return parseStudio(response);
}

export async function patchAdminStudio(
  tenantSlug: string,
  studioId: string,
  request: UpdateStudioRequestDto
): Promise<StudioDto> {
  const parsedRequest = parseUpdateStudioRequest(request);
  const response = await apiPatch<UpdateStudioRequestDto, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/studios/${studioId}`,
    parsedRequest
  );

  return parseStudio(response);
}
